import { CockpitAreaGate } from "@/components/cockpit/CockpitAreaGate";
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { EmptyAreaNote } from "@/components/cockpit/EmptyAreaNote";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";
import { CockpitErrorNotice } from "@/components/cockpit/CockpitErrorNotice";
import { CockpitSkeletonRows } from "@/components/cockpit/CockpitSkeleton";
import { CockpitSearchField } from "@/components/cockpit/CockpitSearchField";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  listMyDownloads,
  getMyDownloadSignedUrl,
  type CustomerDownloadItem,
} from "@/lib/downloads.functions";

export const Route = createFileRoute("/_cockpit/cockpit/downloads")({
  head: () => ({
    meta: [
      { title: "Downloads – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <CockpitAreaGate area="downloads">
      <CockpitDownloadsPage />
    </CockpitAreaGate>
  ),
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "–";
  const units = ["B", "KB", "MB", "GB"];
  let val = n;
  let idx = 0;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx++;
  }
  return `${val.toFixed(val >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function mimeLabel(mime: string | null, filename: string): string {
  const ext = (filename.split(".").pop() ?? "").toLowerCase();
  if (mime) {
    if (mime === "application/pdf") return "PDF";
    if (mime.startsWith("image/")) return "Bild";
    if (mime.startsWith("video/")) return "Video";
    if (mime.startsWith("audio/")) return "Audio";
    if (mime.includes("zip") || mime.includes("compressed")) return "Archiv";
    if (mime.includes("word")) return "Word";
    if (mime.includes("sheet") || mime.includes("excel")) return "Excel";
    if (mime.includes("presentation") || mime.includes("powerpoint"))
      return "PowerPoint";
    if (mime.startsWith("text/")) return "Text";
  }
  if (ext) return ext.toUpperCase();
  return "Datei";
}

function CockpitDownloadsPage() {
  const listFn = useServerFn(listMyDownloads);
  const signFn = useServerFn(getMyDownloadSignedUrl);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [manualOpenIds, setManualOpenIds] = useState<string[] | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit-downloads"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });

  const groups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.title.toLowerCase().includes(q) ||
            it.file_name.toLowerCase().includes(q) ||
            (it.description ?? "").toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [data, search]);

  // Bei aktiver Suche sind alle Treffergruppen offen. Ohne Suche gilt die
  // eigene Auswahl; der erste Bereich ist vorgeöffnet, damit die Seite nicht
  // vollständig zugeklappt startet.
  const openIds = useMemo(() => {
    if (search.trim()) return groups.map((g) => g.category_id);
    if (manualOpenIds !== null) return manualOpenIds;
    return groups.length > 0 ? [groups[0]!.category_id] : [];
  }, [search, groups, manualOpenIds]);

  const visibleCount = useMemo(
    () => groups.reduce((n, g) => n + g.items.length, 0),
    [groups],
  );

  const totalCount = useMemo(
    () => (data ?? []).reduce((n, g) => n + g.items.length, 0),
    [data],
  );

  async function onDownload(item: CustomerDownloadItem) {
    setBusy(item.id);
    try {
      const res = await signFn({ data: { id: item.id } });
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.file_name;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  }

  return (
    <CockpitPageShell>
      <CockpitPageHeader
        title="Downloads"
        description="Alle Dateien, die wir für Sie freigegeben haben – allgemeine Vorlagen und individuelle Ergebnisse Ihres Projekts."
        actions={
          totalCount > 0 ? (
            <CockpitSearchField
              value={search}
              onChange={(next) => {
                setSearch(next);
                setManualOpenIds(null);
              }}
              placeholder="Titel oder Dateiname"
              label="Downloads durchsuchen"
              resultCount={visibleCount}
              resultNoun={{ one: "Datei", many: "Dateien" }}
            />
          ) : undefined
        }
      />

      <CockpitErrorNotice error={error} title="Downloads konnten nicht geladen werden" />

      {isLoading ? (
        <CockpitSkeletonRows rows={3} />
      ) : totalCount === 0 ? (
        /* Ticket KN3: einheitlicher Leerzustand. */
        <EmptyAreaNote
          title="Hier erscheinen Ihre Dateien"
          reason="Für Ihr Konto ist bisher keine Datei freigegeben. Sobald Ihr creategy-Team etwas hinterlegt, finden Sie es hier."
          icon={<FileText className="h-8 w-8" aria-hidden />}
        />
      ) : groups.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground">
          Keine Treffer für „{search}".
        </p>
      ) : (
        <Accordion
          type="multiple"
          value={openIds}
          onValueChange={(v) => {
            if (!search.trim()) setManualOpenIds(v);
          }}
          className="mt-8 w-full"
        >
          {groups.map((g) => (
            <AccordionItem
              key={g.category_id}
              value={g.category_id}
              className="border-foreground/15"
            >
              <AccordionTrigger className="py-5 text-left font-display text-lg font-extrabold text-primary hover:no-underline [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-primary">
                <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 pr-3">
                  <span className="min-w-0 break-words">{g.category_name}</span>
                  <span className="font-body text-xs font-normal text-muted-foreground">
                    {g.items.length} {g.items.length === 1 ? "Datei" : "Dateien"}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <ul className="space-y-2">
                  {g.items.map((it) => (
                    <li
                      key={it.id}
                      className="flex flex-wrap items-start gap-4 rounded-md border border-border bg-card p-4"
                    >
                      <div className="mt-0.5 shrink-0 rounded bg-secondary p-2 text-primary">
                        <FileText className="h-4 w-4" aria-hidden />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="break-words font-display text-base font-extrabold text-primary">
                          {it.title}
                        </p>
                        {it.description && (
                          <p className="mt-1 whitespace-pre-line break-words text-sm text-muted-foreground">
                            {it.description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span className="rounded bg-secondary px-2 py-0.5 font-semibold text-foreground">
                            {mimeLabel(it.mime_type, it.file_name)}
                          </span>
                          <span>{formatBytes(it.size_bytes)}</span>
                          <span aria-hidden>·</span>
                          <span>{dateFmt.format(new Date(it.created_at))}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onDownload(it)}
                        disabled={busy === it.id}
                        aria-label={`${it.title} herunterladen`}
                        className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
                      >
                        {busy === it.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <Download className="h-4 w-4" aria-hidden />
                        )}
                        Herunterladen
                      </button>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </CockpitPageShell>
  );
}
